import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { TimeSeriesAreaChartRecharts } from '../../../../shared/charts/recharts';
import {
  buildSwellDirectionLabelRow,
  buildValueLabelRow,
  enrichedSeriesToTimeSeries,
  formatSwellPeriodChartLabel,
  type EnrichedGraphSeriesPoint,
} from '../../helpers/graph/graphData';
import {
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';
import WeatherTheme from './weather-theme';

export interface SwellChartProps extends WeatherChartBaseProps {
  directionSeries: EnrichedGraphSeriesPoint[];
  periodSeries: EnrichedGraphSeriesPoint[];
}

const SwellChart: FC<SwellChartProps> = ({
  data,
  domain,
  timeZone,
  directionSeries,
  periodSeries,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const labelRows = useMemo(
    () => [
      buildSwellDirectionLabelRow(
        directionSeries,
        periodSeries,
        WeatherTheme.colors.swell,
      ),
      buildValueLabelRow(enrichedSeriesToTimeSeries(periodSeries), (value) =>
        formatSwellPeriodChartLabel(value, unitsConfig),
      ),
    ],
    [directionSeries, periodSeries, unitsConfig],
  );

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  return (
    <TimeSeriesAreaChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      colors={{
        line: WeatherTheme.colors.swell,
        fill: {
          top: WeatherTheme.colors.swellfill,
          bottom: WeatherTheme.colors.swellfill,
        },
        scrubberDot: WeatherTheme.colors.swell,
      }}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default SwellChart;
